import { Category, Service } from "@lemmonhq/types";

export const sortCatalogItems = <T extends Service | Category>(
  items: T[],
): T[] => {
  return items.sort((a, b) => a.order - b.order);
};

export const filterServicesBySchedule = (services: Service[]): Service[] => {
  const now = new Date();
  const currentDay = now.toLocaleDateString("en-US", { weekday: "short" });
  const currentTime = now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });

  return sortCatalogItems(services).filter((service) => {
    // No schedule at all (null, or `{}` / only empty days as the catalog
    // editor saves it) means "Always open", matching what the editor shows.
    const days = service.days;
    const hasSchedule =
      !!days &&
      Object.values(days).some(
        (ranges) => ranges && Object.values(ranges).length > 0,
      );
    if (!days || !hasSchedule) {
      return true;
    }

    let daySchedule = days[currentDay];

    if (!daySchedule || daySchedule.length === 0) {
      return false;
    }

    if (typeof daySchedule === "object" && !Array.isArray(daySchedule)) {
      daySchedule = Object.values(daySchedule);
    }

    return daySchedule.some((schedule) => {
      const start = schedule.start;
      const end = schedule.end;

      if (end < start) {
        return currentTime <= end || currentTime >= start;
      }

      return currentTime >= start && currentTime <= end;
    });
  });
};

export function normalizeFoodTypeName(name?: string) {
  return (name || "")
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[\/,_-]+/g, " ")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function getFoodTypeIconName(name?: string) {
  const normalized = normalizeFoodTypeName(name);

  if (!normalized) {
    return "restaurant";
  }

  const iconMatchers: Array<{ pattern: RegExp; icon: string }> = [
    { pattern: /\b(pizza|pinsa|pinse)\b/, icon: "local_pizza" },
    { pattern: /\b(cookie|cookies|biscuit|biscuits)\b/, icon: "cookie" },
    {
      pattern:
        /\b(cake|cakes|dessert|desserts|dolci|dolcezze|douceur|douceurs|sweet|sweets)\b/,
      icon: "cake",
    },
    { pattern: /\b(ice cream|gelato|shaved ice)\b/, icon: "icecream" },
    {
      pattern:
        /\b(starter|starters|appetizer|appetizers|appetiser|appetisers|entree|entrees|antipasti|antipasto|tapas|vorspeise|aperitif|aperitivi|a partager|to share|share)\b/,
      icon: "soup_kitchen",
    },
    {
      pattern: /\b(salad|salads|salatebi|salade|salades)\b/,
      icon: "nutrition",
    },
    { pattern: /\b(soup|soups)\b/, icon: "soup_kitchen" },
    { pattern: /\b(khinkali|ramen)\b/, icon: "ramen_dining" },
    { pattern: /\b(soba)\b/, icon: "soba" },
    { pattern: /\b(udon)\b/, icon: "udon" },
    { pattern: /\b(pasta|pates|pate)\b/, icon: "dinner_dining" },
    {
      pattern: /\b(taco|tacos|kebab|wrap|panini|panino)\b/,
      icon: "kebab_dining",
    },
    {
      pattern: /\b(bagel|toast|toasts|sandwich|sandwiches)\b/,
      icon: "brunch_dining",
    },
    {
      pattern:
        /\b(burger|fast ?food|snack|snacks|side|sides|garniture|garnitures|contorni|extras|extra)\b/,
      icon: "fastfood",
    },
    {
      pattern:
        /\b(menu|plat du jour|plat principal|hauptgericht|hauptgang|main course|main dishes|main|principal|plat|plats|piatti|assiette|assiettes|food)\b/,
      icon: "brunch_dining",
    },
    {
      pattern: /\b(morning|breakfast|brunch|egg|bagel)\b/,
      icon: "breakfast_dining",
    },
    { pattern: /\b(gluten free|sans gluten)\b/, icon: "wheat" },
    { pattern: /\b(vegan|vegane)\b/, icon: "nutrition" },
    {
      pattern:
        /\b(liquor|alcohol|alcool|alcol|wine|beer|apero|apero time|aperitif|aperitivi|bar)\b/,
      icon: "local_bar",
    },
    { pattern: /\b(coffee|cafe|tea)\b/, icon: "local_cafe" },
    {
      pattern: /\b(drink|drinks|boisson|boissons|beverage|beverages)\b/,
      icon: "local_drink",
    },
    { pattern: /\b(oven|four)\b/, icon: "oven" },
    { pattern: /\b(grill|bbq|barbecue|yakitori)\b/, icon: "outdoor_grill" },
    { pattern: /\b(children|kids|enfants)\b/, icon: "parent_child_dining" },
    { pattern: /\b(hotel)\b/, icon: "room_service" },
    { pattern: /\b(menu)\b/, icon: "set_meal" },
    {
      pattern: /\b(share|sharing|a partager|small plate|petit faim)\b/,
      icon: "dining",
    },
    {
      pattern: /\b(a emporter|emporter|take away|takeaway|to go)\b/,
      icon: "takeout_dining",
    },
    {
      pattern: /\b(appetizer|starter|entree|main|dessert|drink|food)\b/,
      icon: "dining",
    },
  ];

  const match = iconMatchers.find(({ pattern }) => pattern.test(normalized));
  return match?.icon || "restaurant";
}
